import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import Logo from "@/components/Logo";
import {
  ArrowLeft, Mail, MessageSquare, Loader2, CheckCircle2, Send,
} from "lucide-react";

/**
 * Public /contact page.
 *
 * Design notes:
 *   * Single-column, mobile-first. Max-width 640px so it never stretches
 *     awkwardly on desktop. Fully thumb-reachable inputs on 360-430px.
 *   * Honeypot field is positioned OFF-SCREEN via inline styles, not
 *     display:none, because bots increasingly skip display:none inputs.
 *     Real users never see or focus this input.
 *   * Category dropdown pulls its options from GET /api/contact/categories
 *     so it stays authoritative against the backend whitelist.
 *   * Success state locks the form and starts the 60-second cooldown
 *     countdown so a user can’t brute-submit the same email.
 *   * Cooldown 429 from the server hydrates the same cooldown timer, so
 *     the two sources of truth converge on ‘please wait Ns’.
 */

export default function ContactPage() {
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [values, setValues] = useState({
    name: "",
    email: "",
    category: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const honeypotRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.contactCategories();
        if (!cancelled) setCategories(r.data?.categories || []);
      } catch {
        if (!cancelled)
          setCategories([
            "General Enquiry",
            "Business Partnership",
            "Complaint",
            "Billing Issue",
            "Other",
          ]);
      } finally {
        if (!cancelled) setLoadingCats(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Countdown ticker so the CTA can display the exact seconds remaining.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const validate = () => {
    const next = {};
    if (!values.name.trim() || values.name.trim().length < 2) next.name = "Please enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(values.email.trim())) next.email = "Please enter a valid email.";
    if (!values.category) next.category = "Please pick a category.";
    if (!values.message.trim() || values.message.trim().length < 5) next.message = "Please add a short message.";
    if (values.message.length > 4000) next.message = "Message is too long (max 4000 characters).";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || cooldown > 0) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        category: values.category,
        message: values.message.trim(),
        // Off-screen honeypot value — real humans never fill it.
        honeypot: honeypotRef.current?.value || "",
      };
      await api.submitContact(payload);
      setSuccess(true);
      setCooldown(60);
      toast.success("Thanks — we’ve got your message. You’ll hear back by email soon.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) {
        const retry = detail?.retry_after || 60;
        setCooldown(Number(retry) || 60);
        toast.error(detail?.message || "Please wait before submitting another enquiry.");
      } else if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || "Please double-check the form.");
      } else {
        toast.error(detail?.message || detail || "Couldn’t submit right now. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const ctaLabel = useMemo(() => {
    if (submitting) return "Sending…";
    if (cooldown > 0) return `Try again in ${cooldown}s`;
    if (success) return "Send another message";
    return "Send message";
  }, [submitting, cooldown, success]);

  const disabled = submitting || cooldown > 0;

  return (
    <div className="mkt-root mkt-paper-noise min-h-screen" data-testid="contact-page">
      <Toaster richColors position="top-center" />
      {/* Simple mobile-first header — keeps the CityDooh wordmark visible so
          the page feels part of the marketing site. */}
      <header className="border-b-2 border-black/90 bg-[var(--mkt-paper)]/90 backdrop-blur">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 h-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold hover:opacity-80"
            data-testid="contact-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <Link to="/" aria-label="CityDooh home">
            <Logo size={120} light />
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-10 md:py-14">
        <div className="text-center md:text-left mb-8">
          <span className="mkt-tape inline-flex" data-testid="contact-eyebrow">
            <MessageSquare className="h-3.5 w-3.5" /> Contact us
          </span>
          <h1
            className="mt-4 text-4xl md:text-5xl leading-[0.95] uppercase"
            style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
            data-testid="contact-heading"
          >
            Talk to us
          </h1>
          <p className="mt-3 text-sm md:text-base text-black/70 max-w-xl">
            Business partnership, billing question, feature request, or just a
            hello — send it over and we’ll get back within one business day.
          </p>
        </div>

        {success && cooldown > 0 ? (
          <div
            className="rounded-2xl border-2 border-black bg-white/70 px-5 py-6 shadow-[6px_6px_0_#0A0A0A] mb-6 flex items-start gap-3"
            role="status"
            data-testid="contact-success-banner"
          >
            <CheckCircle2 className="h-6 w-6 text-[var(--mkt-yellow-deep)] shrink-0 mt-0.5" />
            <div className="text-sm text-black/80">
              <p className="font-bold">Thanks — we’ve got your message.</p>
              <p className="mt-1">
                A confirmation email is on its way to <strong>{values.email}</strong>.
                Our team will reply from <strong>business@shineadagency.com</strong>.
              </p>
            </div>
          </div>
        ) : null}

        <form
          onSubmit={onSubmit}
          className="rounded-2xl border-2 border-black bg-white/80 p-5 md:p-7 shadow-[8px_8px_0_#0A0A0A] space-y-5"
          noValidate
          data-testid="contact-form"
        >
          {/* Honeypot — off-screen, tab-hidden, autocomplete-off.
              We stamp every possible "ignore me" attribute known to
              password managers (LastPass, 1Password, Bitwarden, Dashlane,
              Chrome autofill, etc.) so legitimate users' password
              managers stop helpfully filling it and causing their
              submissions to be silently dropped. Even the field NAME is
              boring — "xtra_verify_field" doesn't match any autofill
              heuristic. The BACKEND also does secondary validation on
              the value so a false-positive fill won't kill a real
              submission. */}
          <input
            ref={honeypotRef}
            type="text"
            name="xtra_verify_field"
            tabIndex={-1}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-hidden="true"
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
            data-bwignore="true"
            style={{
              position: "absolute",
              left: "-10000px",
              top: "auto",
              width: "1px",
              height: "1px",
              overflow: "hidden",
              opacity: 0,
              pointerEvents: "none",
            }}
            data-testid="contact-honeypot"
          />

          <div className="space-y-1.5">
            <Label htmlFor="cf-name" className="font-bold">Full name</Label>
            <Input
              id="cf-name"
              value={values.name}
              onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
              placeholder="Your name"
              autoComplete="name"
              disabled={disabled}
              className="h-11 border-2 border-black rounded-md"
              data-testid="contact-name-input"
            />
            {errors.name && (
              <p className="text-xs text-destructive" data-testid="contact-name-error">{errors.name}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cf-email" className="font-bold">Email</Label>
            <Input
              id="cf-email"
              type="email"
              inputMode="email"
              value={values.email}
              onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={disabled}
              className="h-11 border-2 border-black rounded-md"
              data-testid="contact-email-input"
            />
            {errors.email && (
              <p className="text-xs text-destructive" data-testid="contact-email-error">{errors.email}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cf-category" className="font-bold">Category</Label>
            <Select
              value={values.category}
              onValueChange={(v) => setValues((val) => ({ ...val, category: v }))}
              disabled={disabled || loadingCats}
            >
              <SelectTrigger id="cf-category" className="h-11 border-2 border-black rounded-md" data-testid="contact-category-trigger">
                <SelectValue placeholder={loadingCats ? "Loading…" : "Pick one…"} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`contact-category-option-${c.replace(/\s+/g, "-").toLowerCase()}`}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && (
              <p className="text-xs text-destructive" data-testid="contact-category-error">{errors.category}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cf-message" className="font-bold">Message</Label>
            <Textarea
              id="cf-message"
              rows={6}
              value={values.message}
              onChange={(e) => setValues((v) => ({ ...v, message: e.target.value }))}
              placeholder="Tell us a little about what you need…"
              disabled={disabled}
              maxLength={4000}
              className="border-2 border-black rounded-md min-h-[140px]"
              data-testid="contact-message-input"
            />
            <div className="flex items-center justify-between">
              {errors.message ? (
                <p className="text-xs text-destructive" data-testid="contact-message-error">{errors.message}</p>
              ) : (
                <span className="text-[11px] text-black/50">Max 4000 characters.</span>
              )}
              <span className="text-[11px] text-black/50 tabular-nums">
                {values.message.length}/4000
              </span>
            </div>
          </div>

          <Button
            type="submit"
            disabled={disabled}
            className="w-full h-12 rounded-full font-bold text-base bg-black text-[var(--mkt-yellow)] hover:bg-black/85 disabled:opacity-70"
            data-testid="contact-submit-button"
          >
            {submitting ? (
              <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {ctaLabel}</span>
            ) : (
              <span className="flex items-center gap-2"><Send className="h-4 w-4" /> {ctaLabel}</span>
            )}
          </Button>

          <p className="text-[11px] text-black/50 text-center">
            By submitting, you agree we may contact you by email regarding this enquiry. No spam.
          </p>
        </form>

        <div className="mt-8 rounded-xl border-2 border-black/20 bg-white/40 p-4 flex items-center gap-3">
          <Mail className="h-4 w-4 text-black/60" />
          <p className="text-xs text-black/70">
            Prefer email? Write to us at{" "}
            <a
              href="mailto:business@shineadagency.com"
              className="font-bold underline"
              data-testid="contact-direct-email-link"
            >
              business@shineadagency.com
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
